/**
 * Warrior kit identity (plan 29 F9, lane BX 2026-10-11).
 *
 * Authored looks for the reachable warrior abilities that resolved to a
 * school default or shared a look with a kit-mate: Whirlwind and Bladestorm
 * were both grey rings; Cleave's slash_arc, Execute's critical_slash and the
 * area/dash impacts all fell to the generic hit_physical flash; Shield Wall
 * had no visible cast. Each def is named by the ability's TOML
 * (`cast_effect` / `impact_effect`), so the local module, the confirmed
 * impact and a remote replay all read the same id.
 *
 * Colours follow each ability's TOML `aura_color` (also the swing colour and
 * the area ring): Whirlwind dust tan, Bladestorm wind-steel blue, Cleave
 * steel blue, Charge orange, Shield Wall steel silver. Bursts end (one-shot
 * tweens); no screen shake or flash (plan 29 F8 — the hit tier owns it).
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, slashArc, shardBurst, glintScatter, groundCloud, shadowedSlash } from './burstBuilders';

const DUST = 0xd9b26f;
const DUST_DEEP = 0x9c7a44;
const WIND = 0x9fd4ff;
const WIND_CORE = 0xe8f6ff;
const CLEAVE = 0x6f9fd8;
const CLEAVE_EDGE = 0xdbe9fa;
const CHARGE = 0xff8800;
const CHARGE_HOT = 0xffd28a;
const STEEL = 0xa8b8c8;
const STEEL_EDGE = 0xf2f6fa;

/** Whirlwind's zone: a dust devil — a tan dust ring with blades whirling inside. */
const WHIRLWIND_DUST_DEVIL: VFXEffectConfig = {
  id: 'whirlwind_dust_devil',
  name: 'Whirlwind Dust Devil',
  category: 'burst',
  description: 'Tan dust ring kicked up at the feet with four steel blades whirling inside it',
  primitives: [
    groundCloud({ width: 120, height: 30, color: DUST, alpha: 0.4, offsetY: 16, swellTo: 1.4, durationMs: 520 }),
    { type: 'ellipse', width: 112, height: 112, color: DUST, alpha: 0.12, depth: 93,
      stroke: { color: DUST, width: 3 },
      tween: { rotation: Math.PI * 2, alpha: 0, duration: 520, ease: 'Sine.easeOut' } },
    // Four blades on the ring, spinning a full turn.
    { type: 'ellipse', width: 34, height: 7, color: 0xf4efe6, alpha: 0.9, depth: 95,
      scatter: { count: 4, radius: 46 },
      tween: { rotation: Math.PI * 2, alpha: 0, duration: 420, ease: 'Power1' } },
    // Dust flecks thrown off the rim.
    { type: 'circle', radius: 3, color: DUST_DEEP, alpha: 0.8, depth: 94,
      scatter: { count: 8, radius: 56 },
      tween: { y: -14, alpha: 0, scale: 0.4, duration: 480, ease: 'Sine.easeOut' } },
  ],
};

/** Whirlwind's hit: a small tan nick and a puff of dust. */
const WHIRLWIND_CUT: VFXEffectConfig = {
  id: 'whirlwind_cut',
  name: 'Whirlwind Cut',
  category: 'burst',
  description: 'A quick tan nick across the target with a dust puff — one spin of the whirlwind',
  primitives: [
    slashArc({ radius: 22, startDeg: -50, endDeg: 30, width: 5, color: 0xf4efe6, durationMs: 180 }),
    slashArc({ radius: 26, startDeg: -50, endDeg: 30, width: 3, color: DUST, alpha: 0.6, durationMs: 220, fadeDelayMs: 40 }),
    groundCloud({ width: 36, height: 12, color: DUST, alpha: 0.4, offsetY: 8, durationMs: 360 }),
  ],
};

/** Bladestorm's hit: two crossing wind-steel cuts — shredded, not nicked. */
const BLADESTORM_SHRED: VFXEffectConfig = {
  id: 'bladestorm_shred',
  name: 'Bladestorm Shred',
  category: 'burst',
  description: 'Two crossing wind-steel cuts and cold glints — the storm shreds what it touches',
  primitives: [
    slashArc({ radius: 24, startDeg: -70, endDeg: 10, width: 5, color: WIND_CORE, durationMs: 200, glow: true }),
    slashArc({ radius: 24, startDeg: 110, endDeg: 190, width: 5, color: WIND, durationMs: 220, fadeDelayMs: 50 }),
    glintScatter({ count: 4, spread: 22, color: WIND_CORE, durationMs: 200, delayMs: 40 }),
  ],
};

/** Cleave's tell: the blade drawn back — a steel ring gathers on the warrior. */
const CLEAVE_WINDUP: VFXEffectConfig = {
  id: 'cleave_windup',
  name: 'Cleave Wind-up',
  category: 'burst',
  description: 'A steel-blue ring gathers on the warrior and its edge brightens: the cleave is coming',
  primitives: [
    { type: 'circle', radius: 48, color: CLEAVE, alpha: 0.3, depth: 93,
      stroke: { color: CLEAVE_EDGE, width: 4 },
      tween: { scale: 0.5, alpha: 0.75, duration: 240, ease: 'Sine.easeIn' } },
    { type: 'circle', radius: 9, color: CLEAVE_EDGE, alpha: 0.35, depth: 96, glow: true,
      tween: { scale: 2.2, alpha: 0.9, duration: 240, ease: 'Sine.easeIn' } },
    { type: 'circle', radius: 3.5, color: CLEAVE_EDGE, alpha: 0.8, depth: 96, glow: true,
      scatter: { count: 6, radius: 40 },
      tween: { scale: 1.6, alpha: 1, duration: 240, ease: 'Sine.easeIn' } },
  ],
};

/** Cleave's hit: a wide steel-blue double sweep and thrown chips. */
const CLEAVE_SWEEP: VFXEffectConfig = {
  id: 'cleave_sweep',
  name: 'Cleave Sweep',
  category: 'burst',
  description: 'A wide steel-blue double sweep across the target with chips thrown off the edge',
  primitives: [
    ...shadowedSlash({
      radius: 34, startDeg: -60, endDeg: 60,
      blade: { color: CLEAVE_EDGE, width: 8, alpha: 0.95, glow: true },
      trail: { color: CLEAVE, width: 5, alpha: 0.6, delayMs: 40 },
      durationMs: 240,
    }),
    slashArc({ radius: 22, startDeg: -50, endDeg: 50, width: 4, color: CLEAVE, alpha: 0.7, durationMs: 220, fadeDelayMs: 60 }),
    shardBurst({ count: 5, spread: 30, color: CLEAVE, shard: { width: 7, height: 2 }, durationMs: 300 }),
  ],
};

/** Charge's arrival hit: an orange shock and kicked-up dust. */
const CHARGE_IMPACT: VFXEffectConfig = {
  id: 'charge_impact',
  name: 'Charge Impact',
  category: 'burst',
  description: 'An orange shock ring where the warrior lands, hot sparks and kicked-up dust',
  primitives: [
    ringBlast({ radius: 18, color: CHARGE, alpha: 0.7, stroke: { color: CHARGE_HOT, width: 3 }, expandTo: 2.3, durationMs: 300 }),
    { type: 'polygon', polygonPoints: [-10, -14, 6, 0, -10, 14, -4, 0], color: CHARGE_HOT, alpha: 0.95, depth: 97,
      tween: { x: 18, alpha: 0, duration: 220, ease: 'Power2' } },
    shardBurst({ count: 6, spread: 28, color: CHARGE_HOT, shard: { width: 6, height: 2 }, durationMs: 280, glow: true }),
    groundCloud({ width: 60, height: 16, color: 0xbbaa88, alpha: 0.4, offsetY: 14, durationMs: 460 }),
  ],
};

/** Shield Wall's cast: a steel tower shield slammed down, with a ward ring. */
const SHIELD_WALL_RAISE: VFXEffectConfig = {
  id: 'shield_wall_raise',
  name: 'Shield Wall Raise',
  category: 'burst',
  description: 'A steel tower shield slams down beside the warrior and a silver ward ring snaps out',
  primitives: [
    { type: 'polygon', polygonPoints: [-12, -26, 12, -26, 14, 10, 0, 28, -14, 10], offsetX: 34, offsetY: -22,
      color: STEEL, alpha: 0.95, depth: 101, stroke: { color: STEEL_EDGE, width: 2 },
      tween: { y: 22, duration: 140, ease: 'Power3' } },
    ringBlast({ radius: 30, color: STEEL, alpha: 0.6, stroke: { color: STEEL_EDGE, width: 4 }, expandTo: 1.8, durationMs: 420, delayMs: 80 }),
    groundCloud({ width: 70, height: 16, color: 0xbbaa88, alpha: 0.35, offsetY: 22, durationMs: 420 }),
    glintScatter({ count: 4, spread: 20, color: STEEL_EDGE, durationMs: 220, delayMs: 120 }),
  ],
};

export const WARRIOR_KIT_VFX: VFXEffectConfig[] = [
  WHIRLWIND_DUST_DEVIL,
  WHIRLWIND_CUT,
  BLADESTORM_SHRED,
  CLEAVE_WINDUP,
  CLEAVE_SWEEP,
  CHARGE_IMPACT,
  SHIELD_WALL_RAISE,
];
